// MasterQuiz.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import "./Quizmaster.css";

const API_BASE = "https://rsa.feetracer.in/app";
const GET_QUIZ_URL = `${API_BASE}/masterquiz.php`;
const SUBMIT_URL = `${API_BASE}/submit_answer.php`;
const START_EXAM_URL = `${API_BASE}/masterquiz.php`;

// Local uploaded test asset path (tooling will convert to a URL)
export const TEST_ASSET_LOCAL_PATH = "/mnt/data/9f043d60-1a48-4096-a64d-0c573d767da3.png";

const MasterQuiz = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  // accept route param or query param as chapter/subject id
  const chapterId = params.chapterId || params.id || urlParams.get("chapter_id") || urlParams.get("id");

  // user from sessionStorage or query param fallback
  let sessionUser = null;
  try { sessionUser = JSON.parse(sessionStorage.getItem("user") || "null"); } catch (e) { sessionUser = null; }
  const queryUserId = urlParams.get("user_id") || urlParams.get("userId");
  const userId = sessionUser?.id || (queryUserId ? Number(queryUserId) : null);

  const [quizMeta, setQuizMeta] = useState(null); // full API response (contains start_time)
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answers, setAnswers] = useState({}); // { questionIndex: selectedIndex }
 const [examTimeLeft, setExamTimeLeft] = useState(0);
   const [markPerQuestion, setMarkPerQuestion] = useState(1);
    const [isConfirmed, setIsConfirmed] = useState(false);
     const [skipped, setSkipped] = useState({});
  const [bookmarked, setBookmarked] = useState({});
 
   const [totalDuration, setTotalDuration] = useState(0); 

  // New: store start_exam_id returned by startExamBackground (if backend returns)
  const [startExamId, setStartExamId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const EXAM_TYPE = "M"; // master exam

    const getTimerClass = () => {
  if (!totalDuration) return "timer-green";

  const halfTime = totalDuration / 2;


  if (examTimeLeft <= 10 * 60) {
    return "timer-red";
  }

  if (examTimeLeft < halfTime) {
    return "timer-amber";
  }


  return "timer-green";
};

  // Prevent leaving the exam
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = (e) => {
      e.preventDefault();
      Swal.fire({
        icon: "warning",
        title: "Navigation Blocked",
        text: "You cannot leave the exam without submitting it.",
      }).then(() => window.history.pushState(null, "", window.location.href));
    };
    const handleKeyDown = (e) => {
      if ((e.key === "F5") || (e.ctrlKey && e.key === "r") || (e.metaKey && e.key === "r")) {
        e.preventDefault();
        Swal.fire({
          icon: "warning",
          title: "Refresh Blocked",
          text: "You cannot reload the exam page.",
        });
      }
    };
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "You cannot leave the exam without submitting.";
    };
    const handleLinkClick = (e) => {
      const target = e.target.closest("a");
      if (target) {
        e.preventDefault();
        Swal.fire({
          icon: "warning",
          title: "Navigation Blocked",
          text: "You cannot leave the exam without submitting it.",
        });
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("click", handleLinkClick, true);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, []);

  // fetch quiz on mount
  useEffect(() => {
    if (!chapterId) {
      setFetchError("Missing chapter/subject id in URL (route or ?chapter_id=).");
      setLoading(false);
      return;
    }
    if (!userId) {
      Swal.fire("Not Logged In", "Please login to start the quiz", "warning").then(() => {
        navigate("/login");
      });
      setLoading(false);
      return;
    }

    const url = `${GET_QUIZ_URL}?chapter_id=${encodeURIComponent(chapterId)}&user_id=${encodeURIComponent(userId)}`;
    console.log("[MasterQuiz] fetching:", url);

    fetch(url)
      .then((res) => res.text())
      .then((text) => {
        const trimmed = text.trim();
        if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
          console.error("[MasterQuiz] non-JSON response:", trimmed.slice(0, 1000));
          throw new Error("Server returned non-JSON. See console for details.");
        }
        return JSON.parse(trimmed);
      })
      .then((data) => {
        if (!data.success) throw new Error(data.message || "Failed to load quiz");

        // Save entire API response (contains start_time) so we can pass it back on submit
        setQuizMeta(data);

        // map API questions (id, question, choices, correctOption, answerDescription, type)
        const mapped = (data.questions || []).map((q) => ({
          id: q.id,
          question: q.question,
          choices: q.choices || q.options || [],
          correctAnswerIndex: typeof q.correctOption === "number" ? q.correctOption : null,
          answerDescription: q.answerDescription || q.answer_description || q.explanation || "",
          type: q.type || "mcq"
        }));
        setQuestions(mapped);

        // set timers if provided
        if (data.duration_seconds) setExamTimeLeft(Number(data.duration_seconds));
        setLoading(false);

        // Start exam background — record start_time on server early (non-blocking)
        startExamBackground(userId, chapterId, EXAM_TYPE);
      })
      .catch((err) => {
        console.error("[MasterQuiz] fetch error:", err);
        setFetchError(err.message || "Failed to load quiz");
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, userId, navigate]);

  // Starts an exam record in background (non-blocking). Backend may return start_exam_id & start_time.
  async function startExamBackground(user_id, chapter_id, exam_type) {
    try {
      const res = await fetch(START_EXAM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "start_exam",
          user_id: user_id,
          chapter_id: Number(chapter_id),
          exam_type: exam_type
        }),
      });
      // defensive: try parse JSON
      const txt = await res.text();
      const t = txt.trim();
      if (!t) return;
      let data;
      try { data = JSON.parse(t); } catch (e) { console.warn("startExamBackground non-json:", t); return; }
      // expected: { success: true, start_exam_id: X, start_time: "..." } or similar
      if (data && data.success) {
        if (data.start_exam_id) setStartExamId(data.start_exam_id);
        if (data.start_time) {
          setStartTime(data.start_time);
          // also save into quizMeta so submit sends the same start_time if needed
          setQuizMeta((prev) => ({ ...(prev || {}), start_time: data.start_time }));
        }
        console.log("[MasterQuiz] start_exam created:", data);
      } else {
        console.warn("[MasterQuiz] start_exam response:", data);
      }
    } catch (err) {
      console.warn("[MasterQuiz] startExamBackground failed:", err);
    }
  }

  // exam timer
  useEffect(() => {
    const examTimer = setInterval(() => {
      setExamTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(examTimer);
          finishExam();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(examTimer);
  }, []);

  

  const formatTime = (secs) => {
    const h = String(Math.floor(secs / 3600)).padStart(2, "0");
    const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
    const s = String(secs % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  // handlers
  const handleSelect = (index) => {
    setSelectedOption(index);
  };

  const confirmAnswer = () => {
    if (selectedOption === null) return;
    setAnswers((prev) => ({ ...prev, [currentQuestion]: selectedOption }));
    nextQuestion();
  };

  const resetAnswer = () => {
    const newAns = { ...answers };
    delete newAns[currentQuestion];
    setAnswers(newAns);
    setSelectedOption(null);
  };
 const nextQuestion = () => {
    // BOOKMARK
    if (
      selectedOption !== null &&
      answers[currentQuestion] === undefined
    ) {
      setBookmarked((prev) => ({
        ...prev,
        [currentQuestion]: true,
      }));
    }

    // SKIPPED
    if (
      selectedOption === null &&
      answers[currentQuestion] === undefined
    ) {
      setSkipped((prev) => ({
        ...prev,
        [currentQuestion]: true,
      }));
    }

    if (currentQuestion >= questions.length - 1) return;

    const nextIndex = currentQuestion + 1;

    setCurrentQuestion(nextIndex);

    const nextAnswer = answers[nextIndex];

    setSelectedOption(
      nextAnswer !== undefined ? nextAnswer : null
    );

    // 🔑 lock only if already answered
    setIsConfirmed(nextAnswer !== undefined);
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      const prev = currentQuestion - 1;
      setCurrentQuestion(prev);
      setSelectedOption(answers[prev] ?? null);
    }
  };

  const jumpToQuestion = (i) => {
    setCurrentQuestion(i);
    setSelectedOption(answers[i] ?? null);
  };

  // finishExam builds and submits payload like ChapterQuiz and includes start_time & start_exam_id if available
  const finishExam = async () => {
    const result = await Swal.fire({
      title: "Submit Exam?",
      html: "Are you sure you want to submit your exam? <br>You won't be able to change answers afterward.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    const answersPayload = questions.map((q, idx) => {
      const uaIndex = answers[idx];

      const attempted = uaIndex !== undefined && uaIndex !== null;
      const isBookmarked = bookmarked[idx] === true;
      const isSkipped = skipped[idx] === true;

      let review = "NA";

      if (attempted) {
        review = "A";
      } else if (isBookmarked) {
        review = "B";
      } else if (isSkipped) {
        review = "S";
      }

      const selected_option = attempted ? `option${uaIndex + 1}` : "";

      return {
        question_id: q.id,
        selected_option,
        review,
      };
    });

    const payload = {
      user_id: userId,
      exam_type: EXAM_TYPE,
      chapter_id: Number(chapterId),
      answers: answersPayload,
      start_time: quizMeta?.start_time ?? startTime ?? null,
      start_exam_id: startExamId ?? null,
    };

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.success) {
        await Swal.fire({
          icon: "success",
          title: "Exam Submitted!",
          text: `Submitted: ${data.submitted_count ?? answersPayload.length}`,
          confirmButtonText: "View Results",
        });

        const sid = data.start_exam_id ?? startExamId ?? "0";
        navigate(`/finish-exam/${sid}`, { state: { ...data } });
      } else {
        Swal.fire("Error", data.message || "Failed to submit answers.", "error");
      }
    } catch (err) {
      console.error("Submit error:", err);
      Swal.fire("Error", "Cannot connect to the server.", "error");
    }
  };

  if (loading) return <div className="loading">Loading quiz...</div>;
  if (fetchError) return <div className="error">Error: {fetchError}</div>;
  if (!questions || questions.length === 0) return <div className="no-questions">No questions available.</div>;

  const q = questions[currentQuestion];

  return (
    <>
      <section>
        <div className="chapter-quiz-wrapper">
       
        <div className="">
           <div className="paper-name">Master Exam - {quizMeta?.subject?.name ?? "Master"}</div>
             <div className="exam-header">
          <div className="exam-summary2">
            <span><b>Total Questions:</b> {questions.length}</span>
            <span style={{ marginLeft: "1rem" }}><b>Total Marks:</b> {questions.length * markPerQuestion}</span>
          </div>
          <button className="finish-btn" onClick={finishExam}>
            Finish Exam
          </button>
           <span className={`exam-timer ${getTimerClass()}`}>
            ⏱ {formatTime(examTimeLeft)}
          </span>
        </div>

        <div className="question-grid">
  {questions.map((_, i) => {

    const answered = answers[i] !== undefined;
    const current = currentQuestion === i;

    // FIX
    const isBookmarked = bookmarked[i] === true;
    const isSkipped = skipped[i] === true;

    return (
      <div
        key={i}
        onClick={() => jumpToQuestion(i)}
        className={`q-box
          ${current ? "current" : ""}
          ${answered ? "answered" : ""}
          ${!answered && isBookmarked ? "bookmarked" : ""}
          ${!answered && !isBookmarked && isSkipped ? "skipped" : ""}
        `}
      >
        {i + 1}
      </div>
    );
  })}
</div>

          <div>
            <div className="question-header">
              Question No: {currentQuestion + 1} — Marks: 2
            </div>
             <div className="question-meta">
  <span
    style={{
      color: "rgb(6 75 169)",
      fontWeight: "bold"
    }}
  >
    Quiz ID:
  </span>

  {" "}

  <strong
    style={{
      color: "rgb(6 75 169)"
    }}
  >
    {q.id}
  </strong>
</div>
            <div className="option-row">
              {q?.choices.map((ch, i) => (
                <label key={i} className="radio-option">
                  <input
                    type="radio"
                    name="option"
                    checked={selectedOption === i}
                    onChange={() => handleSelect(i)}
                  />
                  <span className="opt-label">{String.fromCharCode(65 + i)}</span>
                </label>
              ))}

              <button
                onClick={confirmAnswer}
                className="confirm"
                disabled={selectedOption === null}
              >
                Confirm Ans
              </button>
              <button onClick={resetAnswer} className="reset">
                Reset Ans
              </button>
            </div>

            {selectedOption !== null && (
              <div className="answer-box">
                <strong>Your Answer:</strong>{" "}
                {String.fromCharCode(65 + selectedOption)} — {q.choices[selectedOption]}
              </div>
            )}

            <div className="options-row">
            <div className="question-text">{q?.question}</div>
            {q?.choices.map((ch, i) => (
              <div
                key={i}
                className={`option-item ${answers[currentQuestion] === i ? "selected" : ""}`}
                
              >
                <span className="opt-label">{String.fromCharCode(65 + i)}.</span>
                <span className="opt-text">{ch}</span>
              </div>
            ))}
          </div>

          

            <div className="btn-row">
            <button
              onClick={prevQuestion}
              disabled={currentQuestion === 0}
              className="nav-btn"
            >
              Previous
            </button>
            <button
              onClick={nextQuestion}
              disabled={currentQuestion === questions.length - 1}
              className="nav-btn"
            >
              Next
            </button>
          </div>
          </div>


        </div>
        </div>
      </section>
    </>
  );
};

export default MasterQuiz;
